import { useFancyClothesDesign } from "../../hooks/useFancyClothesDesign";
import {
  dropdownContainerStyles,
  dropdownListStyles,
} from "../../neutral/styles/dropdown";
import {
  SimplifiedSwipePanel,
  type SimplifiedSwipePanelRef,
} from "../../website/components/online-invitation/mobile/SimplifiedSwipePanel";
import { css } from "@emotion/react";
import {
  Button,
  Icon,
  Popover,
  styleUtils,
  theme,
  useMediaQueryState,
  SnackbarAlert,
} from "@minted/minted-components";
import Color from "color";
import isEmpty from "lodash/isEmpty";
import React, { memo, useCallback, useMemo, useRef, useState } from "react";

export enum CalendarType {
  Apple = "apple",
  Google = "google",
  Other = "other",
  Outlook = "outlook",
}

const BUTTON_WIDTH = 215;

type CalendarFancyButtonProps = {
  onClick: (eventArg?: React.MouseEvent) => void;
};

/**
 * FancyButton does not support icons; this mirrors its styling with a
 * left-aligned calendar icon to match the previous minted-components Button.
 */
const CalendarFancyButton = ({ onClick }: CalendarFancyButtonProps) => {
  const { button, designProvider } = useFancyClothesDesign();
  const {
    buttonColor,
    cornerStyle,
    fontFamily,
    fontSizeFactor,
    lineHeightFactor,
  } = button;
  const { backgroundColor } = designProvider;

  const buttonStyles = useMemo(() => {
    let borderRadius = "0";

    if (cornerStyle === "rounded") {
      borderRadius = styleUtils.rem(5);
    } else if (cornerStyle === "round") {
      borderRadius = styleUtils.rem(50);
    }

    const textColor = backgroundColor;
    const bgColor = buttonColor;
    const activeColor = Color(bgColor).darken(0.1).rgb().string();
    const activeTextColor = Color(textColor).lighten(0.08).rgb().string();

    return css`
      align-items: center;
      background-color: ${bgColor};
      background-image: none;
      border-color: transparent;
      border-radius: ${borderRadius};
      border-style: solid;
      border-width: ${styleUtils.rem(1)};
      color: ${textColor};
      cursor: pointer;
      display: flex;
      font-family: ${fontFamily};
      font-size: ${styleUtils.rem(14 * fontSizeFactor)};
      gap: ${theme.spacing.x2};
      justify-content: center;
      line-height: ${lineHeightFactor};
      padding-bottom: ${styleUtils.rem(12)};
      padding-left: ${theme.spacing.x3};
      padding-right: ${theme.spacing.x3};
      padding-top: ${styleUtils.rem(12)};
      text-decoration: none;
      touch-action: manipulation;
      white-space: nowrap;
      width: 100%;

      &:hover,
      &:focus,
      &:active {
        background-color: ${activeColor};
        color: ${activeTextColor};
      }
    `;
  }, [
    backgroundColor,
    buttonColor,
    cornerStyle,
    fontFamily,
    fontSizeFactor,
    lineHeightFactor,
  ]);

  return (
    <button css={buttonStyles} onClick={onClick} type="button">
      <Icon size="small" type={Icon.types.calendar} />
      ADD TO CALENDAR
    </button>
  );
};

const MemoizedCalendarFancyButton = memo(CalendarFancyButton);

const styles = {
  buttonContainer: css`
    width: ${styleUtils.rem(BUTTON_WIDTH)};
  `,
  dropdownListItemText: css`
    ${theme.typeStyles.bodyMediumStyles};
    text-decoration: none;
  `,
  dropdownListWithBorder: css`
    ${dropdownListStyles};
    white-space: normal;
    &:not(:last-child) {
      border-bottom: 1px solid ${theme.colors.gray300};
    }
  `,
  mobileOptionsContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x2};
  `,
  popoverContent: css`
    width: ${styleUtils.rem(BUTTON_WIDTH)};
  `,
  snackbar: css`
    bottom: ${styleUtils.rem(40)};
    left: 50%;
    position: fixed;
    transform: translateX(-50%);
    width: 85%;
    z-index: 1002;
  `,
  wrapper: css`
    display: flex;
    justify-content: center;
    margin-bottom: ${theme.spacing.x3};
    margin-top: ${theme.spacing.x3};
  `,
};

type CalendarTriggerButtonProps = {
  onClick: (eventArg?: React.MouseEvent) => void;
  targetRef?: React.Ref<HTMLDivElement>;
  useThemedButton: boolean;
};

const CalendarTriggerButton = ({
  onClick,
  targetRef,
  useThemedButton,
}: CalendarTriggerButtonProps) => {
  if (useThemedButton) {
    return (
      <div css={styles.buttonContainer} ref={targetRef}>
        <MemoizedCalendarFancyButton onClick={onClick} />
      </div>
    );
  }

  return (
    <div css={styles.buttonContainer} onClick={onClick} ref={targetRef}>
      <Button
        expand
        iconAlignment="left"
        iconType={Icon.types.calendar}
        size="medium"
        text="ADD TO CALENDAR"
        type="secondary"
      />
    </div>
  );
};

type AddToCalendarProps = {
  calendarUrls: Record<string, string>;
  useThemedButton?: boolean;
};

const AddToCalendar = ({
  calendarUrls,
  useThemedButton = false,
}: AddToCalendarProps) => {
  const panelRef = useRef<SimplifiedSwipePanelRef>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const mediumMediaQueryState = useMediaQueryState({
    mediaQuerySize: "medium",
  });
  const isMobile = mediumMediaQueryState === "BELOW";

  const showSuccessSnackbar = useCallback(() => {
    setShowSnackbar(true);
  }, []);

  const dismissSnackbar = useCallback(() => {
    setShowSnackbar(false);
  }, []);

  const handleSelect = useCallback(
    (
      calendarType: CalendarType,
      togglePopover: (eventArg?: React.MouseEvent) => void,
    ) => {
      window.open(calendarUrls[calendarType]);
      togglePopover();
      if (
        calendarType === CalendarType.Apple ||
        calendarType === CalendarType.Other
      ) {
        showSuccessSnackbar();
      }
    },
    [calendarUrls, showSuccessSnackbar],
  );

  const handleMobileSelect = useCallback(
    (calendarType: CalendarType) => {
      window.open(calendarUrls[calendarType]);
      panelRef.current?.close();
      if (
        calendarType === CalendarType.Apple ||
        calendarType === CalendarType.Other
      ) {
        showSuccessSnackbar();
      }
    },
    [calendarUrls, showSuccessSnackbar],
  );

  const handleMobileButtonClick = useCallback(() => {
    panelRef.current?.open();
  }, []);

  if (isEmpty(calendarUrls)) {
    return null;
  }

  if (isMobile) {
    return (
      <div css={styles.wrapper} data-cy="addToCalendarButton">
        <CalendarTriggerButton
          onClick={handleMobileButtonClick}
          useThemedButton={useThemedButton}
        />
        {showSnackbar && (
          <div css={styles.snackbar}>
            <SnackbarAlert
              actionIconType="close"
              actionOnClick={dismissSnackbar}
              modifier="positive"
              text="Calendar file downloaded. Open it to add to your calendar."
            />
          </div>
        )}
        <SimplifiedSwipePanel ref={panelRef} title="Add to...">
          <div css={styles.mobileOptionsContainer}>
            <Button
              expand
              onClick={() => handleMobileSelect(CalendarType.Google)}
              text="Google Calendar"
              type="secondary"
            />
            <Button
              expand
              onClick={() => handleMobileSelect(CalendarType.Apple)}
              text="Apple Calendar"
              type="secondary"
            />
            <Button
              expand
              onClick={() => handleMobileSelect(CalendarType.Outlook)}
              text="Outlook Calendar"
              type="secondary"
            />
            <Button
              expand
              onClick={() => handleMobileSelect(CalendarType.Other)}
              text="Other Calendar"
              type="secondary"
            />
          </div>
        </SimplifiedSwipePanel>
      </div>
    );
  }

  // Desktop experience: Popover
  return (
    <div css={styles.wrapper} data-cy="addToCalendarButton">
      <Popover.PopoverManager position="bottom">
        {({ contentRef, isOpen, targetRef, togglePopover }) => (
          <>
            <CalendarTriggerButton
              onClick={togglePopover}
              targetRef={targetRef}
              useThemedButton={useThemedButton}
            />
            <Popover open={isOpen} ref={contentRef}>
              <Popover.PopoverContent css={styles.popoverContent}>
                <div css={dropdownContainerStyles}>
                  <div
                    css={styles.dropdownListWithBorder}
                    onClick={() =>
                      handleSelect(CalendarType.Google, togglePopover)
                    }
                  >
                    <div css={styles.dropdownListItemText}>Google Calendar</div>
                  </div>
                  <div
                    css={styles.dropdownListWithBorder}
                    onClick={() =>
                      handleSelect(CalendarType.Apple, togglePopover)
                    }
                  >
                    <div css={styles.dropdownListItemText}>Apple Calendar</div>
                  </div>
                  <div
                    css={styles.dropdownListWithBorder}
                    onClick={() =>
                      handleSelect(CalendarType.Outlook, togglePopover)
                    }
                  >
                    <div css={styles.dropdownListItemText}>
                      Outlook Calendar
                    </div>
                  </div>
                  <div
                    css={styles.dropdownListWithBorder}
                    onClick={() =>
                      handleSelect(CalendarType.Other, togglePopover)
                    }
                  >
                    <div css={styles.dropdownListItemText}>Other Calendar</div>
                  </div>
                </div>
              </Popover.PopoverContent>
            </Popover>
          </>
        )}
      </Popover.PopoverManager>
      {showSnackbar && (
        <div css={styles.snackbar}>
          <SnackbarAlert
            actionIconType="close"
            actionOnClick={dismissSnackbar}
            modifier="positive"
            text="Calendar file downloaded. Open it to add to your calendar."
          />
        </div>
      )}
    </div>
  );
};

const MemoizedAddToCalendar = memo(AddToCalendar);

export { MemoizedAddToCalendar as AddToCalendar };

export default MemoizedAddToCalendar;
